import React, { useState, useEffect, useLayoutEffect, useRef, memo } from 'react';
import { Resizable } from 'react-resizable';
import classNames from 'classnames';
import 'react-resizable/css/styles.css';

import style from './style.module.css';

const ResizablePanelComponent = ({
  width: propWidth,
  height: propHeight,
  collapsed,
  onResize,
  onResizeEnd,
  className,
  children,
  ...rest
}) => {
  const [size, setSize] = useState({ width: propWidth, height: propHeight });
  const [headerHeight, setHeaderHeight] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const borderHeight = panel.offsetHeight - panel.clientHeight;

    setHeaderHeight(panel.firstElementChild.offsetHeight + borderHeight);
  }, [collapsed]);

  useEffect(() => {
    setSize({ width: propWidth, height: propHeight });
  }, [propWidth, propHeight]);

  const handleResize = (event, { size: newSize }) => {
    setSize(newSize);
    if (onResize) {
      onResize(event, { size: newSize });
    }
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  const handleResizeStop = (...args) => {
    setIsResizing(false);
    if (onResizeEnd) {
      onResizeEnd(...args);
    }
  };

  const panelClassName = classNames(
    style.host,
    'panel panel-default',
    { [style.collapsed]: collapsed, [style.resizing]: isResizing },
    className,
  );
  const panelStyle = collapsed ? { width: size.width, height: headerHeight ?? 'auto' } : size;

  return (
    <Resizable
      height={size.height}
      minConstraints={[560, 70]}
      onResize={handleResize}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      resizeHandles={collapsed ? [] : ['se']}
      width={size.width}
    >
      <div {...rest} ref={panelRef} className={panelClassName} style={panelStyle}>
        {children}
      </div>
    </Resizable>
  );
};

export const ResizablePanel = memo(ResizablePanelComponent);

export const PanelHeaderComponent = ({ className, children, collapsed, onClose, onToggle, ...rest }) => {
  const headerClassName = classNames(style.header, 'panel-heading', className);

  return (
    <div {...rest} className={headerClassName}>
      {children}
      {onToggle && (
        <button
          aria-label={collapsed ? 'expand' : 'collapse'}
          className={style.toggle}
          onClick={onToggle}
          title={collapsed ? 'Expand' : 'Collapse'}
          type="button"
        >
          <i className={classNames('fa', collapsed ? 'fa-chevron-down' : 'fa-chevron-up')} />
        </button>
      )}
      <button aria-label="close" className="close" onClick={onClose} type="button">
        <span aria-hidden="true"> &times; </span>
      </button>
    </div>
  );
};

export const PanelHeader = memo(PanelHeaderComponent);

export const PanelBodyComponent = ({ className, children, ...rest }) => {
  const bodyClassName = classNames(style.body, 'panel-body', className);

  return (
    <div {...rest} className={bodyClassName}>
      {children}
    </div>
  );
};

export const PanelBody = memo(PanelBodyComponent);
